import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Check, CreditCard, Loader2, LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { OnboardingShell } from '@/features/onboarding/components/onboarding-shell';
import { SocialAccountSourceIcon } from '@/features/social-accounts/components/social-account-source-icon';
import { trackCheckoutCompletedOnce, trackOnboardingEvent } from '@/features/onboarding/analytics';
import { PRICING } from '@/features/onboarding/data';
import {
    useIsUpdatingOnboardingState,
    useOnboardingState,
    useStartOnboardingInstagramConnect,
    useUpdateOnboardingState
} from '@/features/onboarding/hooks/useOnboardingState';
import { onboardingKeys } from '@/features/onboarding/services/queryKeys';
import type { OnboardingStateUpdateInput } from '@/features/onboarding/services/onboarding';
import { formatOnboardingPrice } from '@/features/onboarding/pricing';
import { requireOnboardingStep } from '@/features/onboarding/route-guards';
import { syncOnboardingStoreFromServer, useOnboardingStore } from '@/features/onboarding/store';
import {
    useBillingProducts,
    useCreateCheckoutSession,
    useIsCreatingCheckoutSession
} from '@/features/organizations/hooks/useOrganizationBilling';
import { MIN_CHECKOUT_SOURCE_QUANTITY } from '@/features/organizations/billing-constants';
import { useCurrentOrganizationId } from '@/features/organizations/store/useCurrentOrganizationStore';
import {
    socialAccountKeys,
    useOrganizationSocialAccountSourceLimit
} from '@/features/social-accounts/hooks/useSocialAccounts';
import { apiErrorStatusFallbackPresets, resolveApiErrorMessage } from '@/lib/api-error-translations';

type ConnectSearch = {
    checkout?: 'success' | 'canceled';
    status?: 'success' | 'error';
    message?: string;
};

type PreCheckoutStateInput = Pick<OnboardingStateUpdateInput, 'connectSkipped' | 'currentStep'>;

export const Route = createFileRoute('/onboarding/connect')({
    validateSearch: (search: Record<string, unknown>): ConnectSearch => ({
        checkout: search.checkout === 'success' || search.checkout === 'canceled' ? search.checkout : undefined,
        status: search.status === 'success' || search.status === 'error' ? search.status : undefined,
        message: typeof search.message === 'string' && search.message.trim() !== '' ? search.message : undefined
    }),
    beforeLoad: () => requireOnboardingStep(2),
    component: ConnectRoute
});

export function ConnectRoute() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { checkout: checkoutSearch, status: connectStatusSearch, message: connectMessageSearch } = Route.useSearch();
    const handledCheckoutSearchRef = useRef<string | undefined>(undefined);
    const handledConnectSearchRef = useRef<string | undefined>(undefined);

    const organizationId = useCurrentOrganizationId();

    const sources = useOnboardingStore((state) => state.sources);
    const buildMode = useOnboardingStore((state) => state.buildMode);
    const checkout = useOnboardingStore((state) => state.checkout);
    const connectSkipped = useOnboardingStore((state) => state.connectSkipped);
    const connectedAccountCount = useOnboardingStore((state) => state.connectedAccountCount);
    const setCheckout = useOnboardingStore((state) => state.setCheckout);
    const setConnectSkipped = useOnboardingStore((state) => state.setConnectSkipped);
    const setStep = useOnboardingStore((state) => state.setStep);
    const markCompleted = useOnboardingStore((state) => state.markCompleted);

    const [connectStartError, setConnectStartError] = useState<string | null>(null);

    const awaitingCompletion = checkoutSearch === 'success';

    const { data: serverState } = useOnboardingState({
        refetchInterval: awaitingCompletion ? 2000 : false
    });
    const { data: sourceLimit } = useOrganizationSocialAccountSourceLimit(organizationId);
    const updateState = useUpdateOnboardingState(organizationId);
    const { mutate: updateStateMutate } = updateState;
    const connectStart = useStartOnboardingInstagramConnect(organizationId);
    const { data: billingProducts } = useBillingProducts();
    const checkoutMutation = useCreateCheckoutSession(organizationId, { allowMockFallback: false });
    const isUpdatingOnboardingState = useIsUpdatingOnboardingState(organizationId);
    const isCreatingCheckoutSession = useIsCreatingCheckoutSession(organizationId);

    const recommendedCheckoutQuantity = sourceLimit?.recommendedCheckoutQuantity ?? MIN_CHECKOUT_SOURCE_QUANTITY;
    const billableSources = Math.max(
        sources,
        connectedAccountCount,
        recommendedCheckoutQuantity,
        MIN_CHECKOUT_SOURCE_QUANTITY
    );
    const isCompleted = checkout === 'completed' || Boolean(serverState?.completed);
    const connectCallbackError =
        connectStatusSearch === 'error' ? t('onboardingFlow.connect.errors.callbackFailed') : null;
    const connectError = connectStartError ?? connectCallbackError;

    useEffect(() => {
        if (!connectStatusSearch || handledConnectSearchRef.current === connectStatusSearch || isCompleted) {
            return;
        }

        handledConnectSearchRef.current = connectStatusSearch;

        if (connectStatusSearch === 'success') {
            setConnectSkipped(false);
            updateStateMutate({ connectSkipped: false });

            if (organizationId) {
                void queryClient.invalidateQueries({ queryKey: onboardingKeys.state(organizationId) });
                void queryClient.invalidateQueries({ queryKey: socialAccountKeys.organization(organizationId) });
            }

            void trackOnboardingEvent('ig_connect_succeeded', { accounts: connectedAccountCount });
            void navigate({ to: '/onboarding/connect', search: {} as never, replace: true });
            return;
        }

        void trackOnboardingEvent('ig_connect_failed', { reason: connectMessageSearch ?? 'callback_error' });
    }, [
        connectStatusSearch,
        connectMessageSearch,
        connectedAccountCount,
        isCompleted,
        navigate,
        organizationId,
        queryClient,
        setConnectSkipped,
        updateStateMutate
    ]);

    useEffect(() => {
        if (checkoutSearch !== 'canceled' || handledCheckoutSearchRef.current === checkoutSearch || isCompleted) {
            return;
        }

        handledCheckoutSearchRef.current = checkoutSearch;
        setCheckout('canceled');
        updateStateMutate({ checkoutStatus: 'canceled' });
        void trackOnboardingEvent('checkout_canceled');
        void navigate({ to: '/onboarding/connect', search: {} as never, replace: true });
    }, [checkoutSearch, isCompleted, navigate, setCheckout, updateStateMutate]);

    useEffect(() => {
        if (checkoutSearch !== 'success' || !serverState) {
            return;
        }

        if (organizationId) {
            syncOnboardingStoreFromServer(organizationId, serverState);
        }

        if (!serverState.completed) {
            return;
        }

        const completedSources = Math.max(serverState.sources, serverState.connectedAccounts, 2);
        const completedBuildMode = serverState.buildMode ?? buildMode;

        markCompleted(serverState.completedAt);
        void trackCheckoutCompletedOnce({
            organizationId,
            completedAt: serverState.completedAt,
            sources: completedSources
        });
        void navigate({ to: '/dashboard', search: { welcome: '1', buildMode: completedBuildMode } as never });
    }, [buildMode, checkoutSearch, markCompleted, navigate, organizationId, serverState]);

    async function handleConnect() {
        if (awaitingCompletion || connectStart.isPending || !organizationId) {
            return;
        }

        setConnectStartError(null);
        void trackOnboardingEvent('ig_connect_clicked');

        try {
            const response = await connectStart.mutateAsync();
            window.location.assign(response.authorizationUrl);
        } catch (error) {
            setConnectStartError(
                resolveApiErrorMessage(error, t, {
                    fallbackKey: 'apiErrors.socialAccounts.connectFailed',
                    statusFallbacks: apiErrorStatusFallbackPresets.socialAccountsConnect
                })
            );
            void trackOnboardingEvent('ig_connect_failed', { reason: 'connect_start_failed' });
        }
    }

    async function startCheckout(preCheckoutState: PreCheckoutStateInput = {}) {
        if (
            isCompleted ||
            awaitingCompletion ||
            !organizationId ||
            isCreatingCheckoutSession ||
            isUpdatingOnboardingState
        ) {
            return;
        }

        const product = billingProducts?.find((billingProduct) => billingProduct.isActive) ?? billingProducts?.[0];

        if (!product) {
            toast.error(t('onboardingFlow.connect.checkoutUnavailable'));
            return;
        }

        const currentOnboardingState = useOnboardingStore.getState();
        const retainedSkipState: PreCheckoutStateInput = {
            connectSkipped: currentOnboardingState.connectSkipped,
            ...(currentOnboardingState.connectSkipped ? { currentStep: 'connect' as const } : {})
        };

        const updatedOnboardingState = await updateState
            .mutateAsync({
                ...retainedSkipState,
                ...preCheckoutState,
                checkoutStatus: 'redirected',
                sources: billableSources
            })
            .catch((error) => {
                toast.error(
                    resolveApiErrorMessage(error, t, {
                        fallbackKey: 'apiErrors.validation.invalidRequestBody',
                        statusFallbacks: apiErrorStatusFallbackPresets.onboardingStateUpdate
                    })
                );
                return null;
            });

        if (!updatedOnboardingState) {
            return;
        }

        try {
            const checkoutQuantity = Math.max(
                updatedOnboardingState.sources,
                updatedOnboardingState.connectedAccounts,
                recommendedCheckoutQuantity,
                MIN_CHECKOUT_SOURCE_QUANTITY
            );

            const origin = window.location.origin;
            const response = await checkoutMutation.mutateAsync({
                productId: product.id,
                input: {
                    successUrl: `${origin}/onboarding/connect?checkout=success`,
                    cancelUrl: `${origin}/onboarding/connect?checkout=canceled`,
                    interval: 'monthly',
                    quantity: checkoutQuantity
                }
            });

            void trackOnboardingEvent('checkout_redirected', {
                sources: checkoutQuantity,
                price: billableSources
            });
            window.location.href = response.url;
        } catch (error) {
            toast.error(
                resolveApiErrorMessage(error, t, {
                    fallbackKey: 'apiErrors.billing.checkoutFailed',
                    statusFallbacks: apiErrorStatusFallbackPresets.billingCheckout
                })
            );
        }
    }

    async function skipAndCheckout() {
        if (awaitingCompletion || isCompleted) {
            return;
        }

        setConnectSkipped(true);
        void trackOnboardingEvent('connect_skipped');
        await startCheckout({ connectSkipped: true, currentStep: 'connect' });
    }

    const dashboardBuildMode = serverState?.buildMode ?? buildMode;

    async function backToPersonalize() {
        if (awaitingCompletion || !organizationId || isCreatingCheckoutSession || isUpdatingOnboardingState) {
            return;
        }

        if (isCompleted) {
            void navigate({ to: '/dashboard', search: { welcome: '1', buildMode: dashboardBuildMode } as never });
            return;
        }

        try {
            await updateState.mutateAsync({ currentStep: 'personalize' });
            setStep(1);
        } catch {
            toast.error(t('onboardingFlow.saveFailed'));
        }

        void navigate({ to: '/onboarding/personalize', search: {} as never });
    }

    const isCheckoutBusy = isCreatingCheckoutSession || isUpdatingOnboardingState;
    const isCheckoutActionDisabled = !isCompleted && (isCheckoutBusy || awaitingCompletion);

    return (
        <OnboardingShell currentStep={2} title='' description=''>
            <div className='space-y-8'>
                <Badge variant='muted' appearance='onboarding'>
                    {t('onboardingFlow.connect.badge')}
                </Badge>

                {checkout === 'canceled' ? (
                    <div className='rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground'>
                        {t('onboardingFlow.connect.checkoutCanceled')}
                    </div>
                ) : null}

                {awaitingCompletion && !isCompleted ? (
                    <div className='rounded-xl border bg-muted/20 p-5'>
                        <div className='flex items-start gap-3'>
                            <div className='rounded-full bg-primary/10 p-2 text-primary'>
                                <LoaderCircle className='h-5 w-5 animate-spin' />
                            </div>
                            <div>
                                <p className='font-medium'>{t('onboardingFlow.connect.finalizingTitle')}</p>
                                <p className='mt-1 text-sm text-muted-foreground'>
                                    {t('onboardingFlow.connect.finalizingDescription')}
                                </p>
                            </div>
                        </div>
                    </div>
                ) : null}

                <h2 className='text-3xl font-semibold tracking-tight'>
                    {t('onboardingFlow.connect.headingEmphasis')}
                    <span className='text-muted-foreground'> {t('onboardingFlow.connect.headingRest')}</span>
                </h2>

                <section className='space-y-4'>
                    <SectionLabel>{t('onboardingFlow.connect.requirementsLabel')}</SectionLabel>
                    <div className='space-y-2 rounded-xl border p-4 text-sm'>
                        <RequirementItem>{t('onboardingFlow.connect.requirements.professional')}</RequirementItem>
                        <RequirementItem>{t('onboardingFlow.connect.requirements.page')}</RequirementItem>
                    </div>
                </section>

                <section className='space-y-4 rounded-xl border p-5'>
                    <div className='flex flex-wrap items-center gap-4'>
                        <div className='flex size-9 items-center justify-center rounded-xl border bg-muted/30'>
                            <SocialAccountSourceIcon source='instagram' className='h-7 w-7' />
                        </div>
                        <div className='min-w-0 flex-1 space-y-1'>
                            <div className='text-sm font-semibold'>{t('onboardingFlow.connect.connectionTitle')}</div>
                            <div className='font-mono text-xs text-muted-foreground'>
                                {t('onboardingFlow.connect.connectedCounter', {
                                    connected: connectedAccountCount > 0 ? 1 : 0,
                                    sources
                                })}
                            </div>
                        </div>
                        <Button
                            type='button'
                            variant={connectedAccountCount > 0 ? 'outline' : 'default'}
                            disabled={awaitingCompletion || connectStart.isPending || !organizationId}
                            onClick={() => void handleConnect()}
                        >
                            {connectStart.isPending ? <Loader2 className='h-4 w-4 animate-spin' /> : null}
                            {!connectStart.isPending && connectedAccountCount > 0 ? (
                                <Check className='h-4 w-4' />
                            ) : null}
                            {connectStart.isPending
                                ? t('onboardingFlow.connect.ctaWaiting')
                                : connectedAccountCount > 0
                                  ? t('onboardingFlow.connect.ctaConnected')
                                  : t('onboardingFlow.connect.ctaConnectShort')}
                        </Button>
                    </div>
                    <p className='text-sm leading-6 text-muted-foreground'>
                        {t('onboardingFlow.connect.trust.secureDescription')}
                    </p>
                    {connectError ? (
                        <div className='rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
                            <p className='font-semibold'>{t('onboardingFlow.connect.errorTitle')}</p>
                            <p className='mt-1'>{connectError}</p>
                        </div>
                    ) : null}
                </section>

                <DarkPriceStrip
                    price={formatOnboardingPrice(billableSources, t)}
                    sources={billableSources}
                    isCompleted={isCompleted}
                    connectSkipped={connectSkipped}
                />

                <div className='border-t pt-5'>
                    <div className='flex flex-wrap items-center gap-y-3'>
                        <Button
                            type='button'
                            variant='quiet'
                            disabled={isCheckoutActionDisabled}
                            onClick={() => void backToPersonalize()}
                        >
                            {t('onboardingFlow.back')}
                        </Button>
                        {!isCompleted ? (
                            <Button
                                type='button'
                            variant='quietLink'
                            className='ml-auto'
                                disabled={isCheckoutActionDisabled}
                                onClick={() => void skipAndCheckout()}
                            >
                                {t('onboardingFlow.connect.skip')}
                            </Button>
                        ) : null}
                        <div className='w-full sm:ml-6 sm:w-auto'>
                            <Button
                                type='button'
                                size='lg'
                                className='w-full sm:w-auto'
                                disabled={isCheckoutActionDisabled}
                                onClick={() => {
                                    if (isCompleted) {
                                        void navigate({
                                            to: '/dashboard',
                                            search: { welcome: '1', buildMode: dashboardBuildMode } as never
                                        });
                                        return;
                                    }

                                    void startCheckout();
                                }}
                            >
                                {isCompleted
                                    ? t('onboardingFlow.connect.nextToDashboard')
                                    : t('onboardingFlow.connect.checkoutCta', { days: PRICING.trialDays })}
                                <ArrowRight className='h-4 w-4' />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </OnboardingShell>
    );
}

function SectionLabel({ children }: { children: string }) {
    return <div className='font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground'>{children}</div>;
}

function RequirementItem({ children }: { children: string }) {
    return (
        <div className='flex items-start gap-2'>
            <Check className='mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400' />
            <span>{children}</span>
        </div>
    );
}

function DarkPriceStrip({
    price,
    sources,
    isCompleted,
    connectSkipped
}: {
    price: string;
    sources: number;
    isCompleted: boolean;
    connectSkipped: boolean;
}) {
    const { t } = useTranslation();

    return (
        <div className='relative overflow-hidden rounded-xl bg-[linear-gradient(90deg,#0b0b12_0%,#171229_42%,#23204a_100%)] p-5 text-white'>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute -top-10 right-6 h-36 w-36 rounded-full bg-indigo-500/20 blur-3xl'
            />
            <div
                aria-hidden='true'
                className='pointer-events-none absolute -bottom-14 left-20 h-40 w-40 rounded-full bg-violet-500/12 blur-3xl'
            />
            <div className='relative flex flex-wrap items-start justify-between gap-5'>
                <div>
                    <div className='flex items-end gap-2'>
                        <div className='text-2xl font-semibold'>{price}</div>
                        <div className='pb-0.5 text-sm font-normal text-white/50'>
                            {t('onboardingFlow.connect.afterTrial')}
                        </div>
                    </div>
                    <div className='mt-2 font-mono text-xs text-white/50'>
                        {t('onboardingFlow.connect.priceStripBreakdown', { sources })}
                    </div>
                    <div className='mt-3 flex flex-wrap gap-2'>
                        <Badge
                            variant='inverse'
                            className='[&_svg]:mr-1.5'
                        >
                            <CreditCard className='h-3.5 w-3.5 shrink-0' />
                            {isCompleted
                                ? t('onboardingFlow.connect.completedBadge')
                                : t('onboardingFlow.connect.trialBadge')}
                        </Badge>
                        {connectSkipped ? (
                            <Badge variant='inverse'>
                                {t('onboardingFlow.connect.connectLaterBadge')}
                            </Badge>
                        ) : null}
                    </div>
                </div>
                <div className='max-w-[180px] text-right font-mono text-xs text-white/50'>
                    {t('onboardingFlow.connect.priceStripTerms')}
                </div>
            </div>
        </div>
    );
}
